import React, { useState, useEffect } from 'react';
import { Search, Upload, Download, RefreshCw, Package, FileText, Calendar, MapPin, Wrench, CheckCircle, AlertCircle, Filter, X, Cable, Edit, Trash2, Plus } from 'lucide-react';

const NRMCInventory = ({ user, onCountsChange }) => {
  const [inventory, setInventory] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploadMode, setUploadMode] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [previewData, setPreviewData] = useState(null);       // /preview response payload
  const [previewToken, setPreviewToken] = useState(null);     // token to commit
  const [committing, setCommitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [sortField, setSortField] = useState('inserttime');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    complete: '',
    manufacturer: '',
    location: '',
    assetType: ''
  });

  // Summary stats
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    pending: 0,
    manufacturers: 0,
    locations: 0
  });

  // Parts modal states
  const [showPartsModal, setShowPartsModal] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [equipmentParts, setEquipmentParts] = useState([]);
  const [partsLoading, setPartsLoading] = useState(false);
  const [showPartForm, setShowPartForm] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [partFormData, setPartFormData] = useState({
    part_description: '',
    part_number: '',
    part_manufacturer: '',
    quantity_per_unit: 1,
    unit_cost: '',
    supplier: '',
    supplier_link: '',
    notes: ''
  });

  // Part search results (for enhanced search)
  const [partSearchResults, setPartSearchResults] = useState(null);

  // Active tab for clickable tiles (all, completed, pending)
  const [activeTab, setActiveTab] = useState('all');

  // Parts counts cache (keyed by type+manufacturer+model)
  const [partsCounts, setPartsCounts] = useState({});

  // Fetch inventory data
  const fetchInventory = async () => {
    setLoading(true);
    try {
      console.log('=== FETCHING NRMC INVENTORY ===');
      const response = await fetch('/api/nrmc-inventory');
      const data = await response.json();
      
      console.log('Inventory data received:', data);
      setInventory(data.data || []);
      calculateStats(data.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching NRMC inventory:', error);
      setLoading(false);
      setInventory([]);
    }
  };

  // Calculate summary statistics
  const calculateStats = (data) => {
    const total = data.length;
    const completed = data.filter(item => item.complete === 'YES').length;
    const pending = total - completed;
    const manufacturers = new Set(data.map(item => item['asset.asset_manufacturer']).filter(Boolean)).size;
    const locations = new Set(data.map(item => item.work_location).filter(Boolean)).size;

    setStats({
      total,
      completed,
      pending,
      manufacturers,
      locations
    });

    // Update parent counts
    if (onCountsChange) {
      onCountsChange();
    }
  };

  // Batch fetch parts counts for current page items
  const fetchPartsCountsForItems = async (items) => {
    if (!items || items.length === 0) return;

    // Create unique keys for equipment types
    const uniqueEquipment = new Map();
    items.forEach(item => {
      if (item.type && item['asset.asset_manufacturer']) {
        const key = `${item.type}|${item['asset.asset_manufacturer']}|${item.model || ''}`;
        uniqueEquipment.set(key, {
          type: item.type,
          manufacturer: item['asset.asset_manufacturer'],
          model: item.model || ''
        });
      }
    });

    // Fetch counts for each unique equipment type
    const newCounts = { ...partsCounts };
    const promises = Array.from(uniqueEquipment.entries()).map(async ([key, equipment]) => {
      // Skip if we already have this count cached
      if (newCounts[key] !== undefined) return;

      try {
        const response = await fetch(
          `/api/equipment-parts/count?type=${encodeURIComponent(equipment.type)}&manufacturer=${encodeURIComponent(equipment.manufacturer)}&model=${encodeURIComponent(equipment.model)}`
        );
        const data = await response.json();
        if (data.success) {
          newCounts[key] = data.count;
        } else {
          newCounts[key] = 0;
        }
      } catch (error) {
        console.error('Error fetching part count:', error);
        newCounts[key] = 0;
      }
    });

    await Promise.all(promises);
    setPartsCounts(newCounts);
  };

  // Filter and sort inventory
// Fixed search function in NRMCInventory.js - replace the filterAndSortInventory function

const filterAndSortInventory = () => {
  let filtered = [...inventory];

  // Apply tab filter (all, completed, pending)
  if (activeTab === 'completed') {
    filtered = filtered.filter(item => item.complete === 'YES');
  } else if (activeTab === 'pending') {
    filtered = filtered.filter(item => item.complete !== 'YES');
  }
  // If activeTab === 'all', show everything (no filter)

  // Apply part search filter - show only equipment that uses the found parts
  if (partSearchResults && partSearchResults.length > 0) {
    filtered = filtered.filter(item => {
      // Check if this inventory item matches any of the found parts
      return partSearchResults.some(part => {
        // Case-insensitive matching with null/undefined handling
        const itemType = (item.type || '').toLowerCase().trim();
        const partType = (part.equipment_type || '').toLowerCase().trim();
        const itemMfg = (item['asset.asset_manufacturer'] || '').toLowerCase().trim();
        const partMfg = (part.equipment_manufacturer || '').toLowerCase().trim();
        const itemModel = (item.model || '').toLowerCase().trim();
        const partModel = (part.equipment_model || '').toLowerCase().trim();
        
        // Type must match exactly (case-insensitive)
        const typeMatch = itemType === partType;
        
        // Manufacturer: check if either contains the other (handles "Philips" vs "Philips Healthcare")
        const mfgMatch = itemMfg.includes(partMfg) || partMfg.includes(itemMfg);
        
        // Model: must match if part has a model specified, otherwise matches all models
        const modelMatch = !partModel || partModel === '' || itemModel === partModel;
        
        return typeMatch && mfgMatch && modelMatch;
      });
    });
  }

  // Apply search filter - ONLY if we're NOT doing a part search
  // (Part search already filtered by equipment type/mfg/model)
  if (searchTerm && !partSearchResults) {
    filtered = filtered.filter(item =>
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||                    // Asset tag (displayed)
      item.number_field?.toLowerCase().includes(searchTerm.toLowerCase()) ||           // System number  
      item['asset.asset_manufacturer']?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.serial?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.work_location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.vicinity?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  // Apply advanced filters
  if (filters.complete) {
    filtered = filtered.filter(item => {
      if (filters.complete === 'yes') return item.complete === 'YES';
      if (filters.complete === 'no') return item.complete !== 'YES';
      return true;
    });
  }

  if (filters.manufacturer) {
    filtered = filtered.filter(item => 
      item['asset.asset_manufacturer']?.toLowerCase().includes(filters.manufacturer.toLowerCase())
    );
  }

  if (filters.location) {
    filtered = filtered.filter(item => 
      item.work_location?.toLowerCase().includes(filters.location.toLowerCase())
    );
  }

  if (filters.assetType) {
    filtered = filtered.filter(item => 
      item.type?.toLowerCase().includes(filters.assetType.toLowerCase())
    );
  }

  // Apply sorting
  filtered.sort((a, b) => {
    let aVal = a[sortField] || '';
    let bVal = b[sortField] || '';
    
    if (sortField === 'inserttime' || sortField === 'u_next_scheduled_maint_date') {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    }
    
    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  setFilteredInventory(filtered);
  setCurrentPage(1);
};

  // Field mapping is fixed for the TeamChamps CSV format.
  const nrmcFieldMapping = {
    'asset': 'number',                          // Asset number for duplicate checking
    'name': 'name',                             // Asset tag for display
    'asset.asset_manufacturer': 'asset_manufacturer',  // Manufacturer
    'serial': 'serial_number',                  // Serial Number
    'model': 'model_name',                      // Model
    'type': 'asset_type',                       // Asset Type
    'work_location': 'asset_location',          // Location
    'vicinity': 'u_location_vicinity',          // Vicinity
    'u_next_scheduled_maint_date': 'u_next_scheduled_maint_date'  // PM Date
  };

  const resetImportState = () => {
    setUploadFile(null);
    setUploadProgress(null);
    setPreviewData(null);
    setPreviewToken(null);
    setCommitting(false);
  };

  // Phase 1 of import: send CSV to /preview. Response either returns
  // a preview payload + preview_token, or a 400 with a hard-block message
  // (delete would exceed 30% of current inventory).
  const handleFileUpload = async (event) => {
    event.preventDefault();

    if (!uploadFile) {
      alert('Please select a CSV file');
      return;
    }

    const formData = new FormData();
    formData.append('csvFile', uploadFile);
    formData.append('fieldMapping', JSON.stringify(nrmcFieldMapping));

    try {
      setUploadProgress({ status: 'uploading', message: 'Analyzing CSV — computing what would change...' });

      const response = await fetch('/api/nrmc-inventory/upload/preview', {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (response.status === 400 && result.preview) {
        // Hard block: >30% delete. Server returned counts alongside the error.
        setUploadProgress({
          status: 'error',
          message: result.error
        });
        setPreviewData(null);
        setPreviewToken(null);
        return;
      }

      if (!response.ok || !result.success) {
        setUploadProgress({
          status: 'error',
          message: `Preview failed: ${result.error || 'Unknown error'}`
        });
        return;
      }

      // Success — show preview panel, await user confirmation.
      setPreviewData(result.preview);
      setPreviewToken(result.preview_token);
      setUploadProgress(null);
    } catch (error) {
      console.error('Preview error:', error);
      setUploadProgress({
        status: 'error',
        message: `Preview failed: ${error.message}`
      });
    }
  };

  // Phase 2 of import: user confirmed the preview, execute the commit
  // with the token. Same success-message rendering as the old single-step
  // upload used.
  const handleCommitImport = async () => {
    if (!previewToken) return;

    try {
      setCommitting(true);
      setUploadProgress({ status: 'uploading', message: 'Applying import — updating, adding, and deleting devices...' });

      const response = await fetch('/api/nrmc-inventory/upload/commit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preview_token: previewToken })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        setUploadProgress({
          status: 'error',
          message: `Import failed: ${result.error || 'Unknown error'}`
        });
        setCommitting(false);
        return;
      }

      let successMessage = `✅ Import Complete!\n\n`;
      if (result.processedRows > 0)     successMessage += `📦 New Items: ${result.processedRows} added\n`;
      if (result.updatedRows > 0)       successMessage += `🔄 Updated Items: ${result.updatedRows} existing records updated\n`;
      if (result.deletedRows > 0)       successMessage += `🗑️  Deleted Items: ${result.deletedRows} removed (not in upload)\n`;
      if (result.fixedAssetsCreated > 0) successMessage += `🏢 Fixed Assets Created: ${result.fixedAssetsCreated}\n`;
      if (result.fixedAssetsUpdated > 0) successMessage += `🔄 Fixed Assets Updated: ${result.fixedAssetsUpdated}\n`;
      if (result.deletedFixedAssets > 0) successMessage += `🗑️  Fixed Assets Deleted: ${result.deletedFixedAssets}\n`;
      if (result.duplicateRows > 0)     successMessage += `⏭️  Skipped (no changes): ${result.duplicateRows}\n`;
      if (result.fixedAssetErrors > 0)  successMessage += `⚠️  Fixed Asset Errors: ${result.fixedAssetErrors} (check console)\n`;
      if (result.deletionErrors > 0)    successMessage += `⚠️  Deletion Errors: ${result.deletionErrors} (check console)\n`;
      if (result.errorRows > 0)         successMessage += `❌ Errors: ${result.errorRows} rows failed\n`;

      const totalProcessed = (result.processedRows || 0) + (result.updatedRows || 0) + (result.deletedRows || 0);
      successMessage += `\n🎉 Total: ${totalProcessed} assets processed!`;

      setUploadProgress({ status: 'success', message: successMessage });
      setPreviewData(null);
      setPreviewToken(null);
      setCommitting(false);

      setTimeout(() => {
        fetchInventory();
        setUploadMode(false);
        resetImportState();
      }, 4000);
    } catch (error) {
      console.error('Commit error:', error);
      setUploadProgress({
        status: 'error',
        message: `Import failed: ${error.message}`
      });
      setCommitting(false);
    }
  };

  // Mark item as complete
  const markComplete = async (itemId) => {
    if (!window.confirm('Mark this inventory item as complete?')) {
      return;
    }

    try {
      const response = await fetch(`/api/nrmc-inventory/${itemId}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.id,
          userName: user.fullName
        })
      });

      const result = await response.json();

      if (result.success) {
        fetchInventory(); // Refresh data
      } else {
        alert('Error: ' + result.error);
      }
    } catch (error) {
      console.error('Mark complete error:', error);
      alert('Error: ' + error.message);
    }
  };

  // ===== PARTS MANAGEMENT FUNCTIONS =====

  // Open parts modal for an equipment item
  const openPartsModal = async (item) => {
    setSelectedEquipment(item);
    setShowPartsModal(true);
    await fetchPartsForEquipment(item);
  };

  // Fetch parts for specific equipment
  const fetchPartsForEquipment = async (item) => {
    setPartsLoading(true);
    try {
      const response = await fetch(
        `/api/equipment-parts/for-equipment?type=${encodeURIComponent(item.type)}&manufacturer=${encodeURIComponent(item['asset.asset_manufacturer'])}&model=${encodeURIComponent(item.model || '')}`
      );
      const data = await response.json();
      
      if (data.success) {
        setEquipmentParts(data.data || []);
      }
      setPartsLoading(false);
    } catch (error) {
      console.error('Error fetching parts:', error);
      setPartsLoading(false);
      setEquipmentParts([]);
    }
  };

  // Get part count for equipment (for badge)
  const getPartCount = async (item) => {
    try {
      const response = await fetch(
        `/api/equipment-parts/count?type=${encodeURIComponent(item.type)}&manufacturer=${encodeURIComponent(item['asset.asset_manufacturer'])}&model=${encodeURIComponent(item.model || '')}`
      );
      const data = await response.json();
      return data.success ? data.count : 0;
    } catch (error) {
      return 0;
    }
  };

  // Open part form for adding/editing
  const openPartForm = (part = null) => {
    if (part) {
      setEditingPart(part);
      setPartFormData({
        part_description: part.part_description || '',
        part_number: part.part_number || '',
        part_manufacturer: part.part_manufacturer || '',
        quantity_per_unit: part.quantity_per_unit || 1,
        unit_cost: part.unit_cost || '',
        supplier: part.supplier || '',
        supplier_link: part.supplier_link || '',
        notes: part.notes || ''
      });
    } else {
      setEditingPart(null);
      setPartFormData({
        part_description: '',
        part_number: '',
        part_manufacturer: '',
        quantity_per_unit: 1,
        unit_cost: '',
        supplier: '',
        supplier_link: '',
        notes: ''
      });
    }
    setShowPartForm(true);
  };

  // Close part form
  const closePartForm = () => {
    setShowPartForm(false);
    setEditingPart(null);
    setPartFormData({
      part_description: '',
      part_number: '',
      part_manufacturer: '',
      quantity_per_unit: 1,
      unit_cost: '',
      supplier: '',
      supplier_link: '',
      notes: ''
    });
  };

  // Handle part form input change
  const handlePartFormChange = (e) => {
    const { name, value } = e.target;
    setPartFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Submit part form (add or edit)
  const submitPartForm = async (e) => {
    e.preventDefault();

    if (!selectedEquipment) return;

    try {
      const payload = {
        equipment_type: selectedEquipment.type,
        equipment_manufacturer: selectedEquipment['asset.asset_manufacturer'],
        equipment_model: selectedEquipment.model || null,
        ...partFormData,
        userId: user.id,
        userName: user.fullName
      };

      const url = editingPart 
        ? `/api/equipment-parts/${editingPart.id}`
        : '/api/equipment-parts';
      
      const method = editingPart ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result.success) {
        closePartForm();
        await fetchPartsForEquipment(selectedEquipment);
        
        // Invalidate parts count cache for this equipment type
        const cacheKey = `${selectedEquipment.type}|${selectedEquipment['asset.asset_manufacturer']}|${selectedEquipment.model || ''}`;
        setPartsCounts(prev => {
          const newCounts = { ...prev };
          delete newCounts[cacheKey];
          return newCounts;
        });
        // Refetch to update badge
        fetchPartsCountsForItems(currentItems);
        
        alert(`Part ${editingPart ? 'updated' : 'added'} successfully!`);
      } else {
        alert('Error: ' + result.error);
      }
    } catch (error) {
      console.error('Part form error:', error);
      alert('Error: ' + error.message);
    }
  };

  // Delete part
  const deletePart = async (partId) => {
    if (!window.confirm('Are you sure you want to delete this part?')) {
      return;
    }

    try {
      const response = await fetch(`/api/equipment-parts/${partId}`, {
        method: 'DELETE'
      });

      const result = await response.json();

      if (result.success) {
        await fetchPartsForEquipment(selectedEquipment);
        
        // Invalidate parts count cache for this equipment type
        const cacheKey = `${selectedEquipment.type}|${selectedEquipment['asset.asset_manufacturer']}|${selectedEquipment.model || ''}`;
        setPartsCounts(prev => {
          const newCounts = { ...prev };
          delete newCounts[cacheKey];
          return newCounts;
        });
        // Refetch to update badge
        fetchPartsCountsForItems(currentItems);
        
        alert('Part deleted successfully!');
      } else {
        alert('Error: ' + result.error);
      }
    } catch (error) {
      console.error('Delete part error:', error);
      alert('Error: ' + error.message);
    }
  };

  // Enhanced search that searches both inventory AND parts
  const handleSearch = async (term) => {
    setSearchTerm(term);
    
    // If search term has numbers (likely a part number), also search parts
    if (term.length >= 3 && /[0-9]/.test(term)) {
      try {
        const response = await fetch(`/api/equipment-parts/search?query=${encodeURIComponent(term)}`);
        const data = await response.json();
        
        if (data.success && data.data.length > 0) {
          setPartSearchResults(data.data);
        } else {
          setPartSearchResults(null);
        }
      } catch (error) {
        console.error('Part search error:', error);
        setPartSearchResults(null);
      }
    } else {
      setPartSearchResults(null);
    }
  };

  // Clear part search
  const clearPartSearch = () => {
    setPartSearchResults(null);
    setSearchTerm('');
  };

  // ===== END PARTS MANAGEMENT FUNCTIONS =====

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      complete: '',
      manufacturer: '',
      location: '',
      assetType: ''
    });
    setSearchTerm('');
  };

  // useEffect hooks
  useEffect(() => {
    fetchInventory();
  }, []);

  useEffect(() => {
    filterAndSortInventory();
  }, [inventory, searchTerm, sortField, sortDirection, filters, activeTab, partSearchResults]);

  // Helper functions
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  // Pagination
  const totalPages = Math.ceil(filteredInventory.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredInventory.slice(startIndex, endIndex);

  // Fetch parts counts when current page items change
  useEffect(() => {
    fetchPartsCountsForItems(currentItems);
  }, [currentItems.length, currentPage, activeTab]); // Added activeTab so it refetches when tab changes

  // PartsBadge component - shows parts count and is clickable
  const PartsBadge = ({ item, partCount, onClick }) => {
    if (partCount === undefined) {
      // Still loading
      return (
        <div style={{ fontSize: '0.75rem', color: '#9ca3af', textAlign: 'center' }}>...</div>
      );
    }

    if (partCount === 0) {
      // No parts documented yet
      return (
        <button
          onClick={onClick}
          style={{
            padding: '0.25rem 0.5rem',
            background: '#f3f4f6',
            color: '#9ca3af',
            border: '1px dashed #d1d5db',
            borderRadius: '0.375rem',
            cursor: 'pointer',
            fontSize: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            margin: '0 auto'
          }}
          title="Add parts for this equipment"
        >
          <Cable size={12} />
          <Plus size={10} />
        </button>
      );
    }

    // Has parts - show count
    return (
      <button
        onClick={onClick}
        style={{
          padding: '0.25rem 0.5rem',
          background: '#dbeafe',
          color: '#1e40af',
          border: '1px solid #3b82f6',
          borderRadius: '0.375rem',
          cursor: 'pointer',
          fontSize: '0.75rem',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
          margin: '0 auto'
        }}
        title={`${partCount} part${partCount !== 1 ? 's' : ''} documented`}
      >
        <Cable size={12} />
        {partCount}
      </button>
    );
  };

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <h1 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem' }}>NRMC Inventory</h1>
          <p style={{ color: '#6b7280' }}>Full inventory management for Natchitoches Regional Medical Center</p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{ 
              display: 'flex', alignItems: 'center', gap: '0.5rem', 
              padding: '0.5rem 1rem', background: '#6b7280', color: 'white', 
              border: 'none', borderRadius: '0.375rem', cursor: 'pointer' 
            }}
          >
            <Filter size={16} />
            Filters
          </button>
          <button
            onClick={() => setUploadMode(!uploadMode)}
            style={{ 
              display: 'flex', alignItems: 'center', gap: '0.5rem', 
              padding: '0.5rem 1rem', background: '#2563eb', color: 'white', 
              border: 'none', borderRadius: '0.375rem', cursor: 'pointer' 
            }}
          >
            <Upload size={16} />
            Upload CSV
          </button>
          <button
            onClick={fetchInventory}
            style={{ 
              display: 'flex', alignItems: 'center', gap: '0.5rem', 
              padding: '0.5rem 1rem', background: '#16a34a', color: 'white', 
              border: 'none', borderRadius: '0.375rem', cursor: 'pointer' 
            }}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Stats - Clickable Tabs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
        {/* Total Items Tile */}
        <button
          onClick={() => setActiveTab('all')}
          style={{ 
            background: activeTab === 'all' ? '#2563eb' : 'white', 
            padding: '1.5rem', 
            borderRadius: '0.5rem', 
            border: activeTab === 'all' ? '2px solid #2563eb' : '1px solid #e5e7eb',
            cursor: 'pointer',
            transition: 'all 0.2s',
            textAlign: 'left'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <Package size={20} color={activeTab === 'all' ? 'white' : '#2563eb'} />
            <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: activeTab === 'all' ? 'white' : '#6b7280' }}>Total Items</h3>
          </div>
          <p style={{ fontSize: '2rem', fontWeight: '700', color: activeTab === 'all' ? 'white' : '#111827' }}>{stats.total}</p>
        </button>
        
        {/* Completed Tile */}
        <button
          onClick={() => setActiveTab('completed')}
          style={{ 
            background: activeTab === 'completed' ? '#16a34a' : 'white', 
            padding: '1.5rem', 
            borderRadius: '0.5rem', 
            border: activeTab === 'completed' ? '2px solid #16a34a' : '1px solid #e5e7eb',
            cursor: 'pointer',
            transition: 'all 0.2s',
            textAlign: 'left'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <CheckCircle size={20} color={activeTab === 'completed' ? 'white' : '#16a34a'} />
            <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: activeTab === 'completed' ? 'white' : '#6b7280' }}>Completed</h3>
          </div>
          <p style={{ fontSize: '2rem', fontWeight: '700', color: activeTab === 'completed' ? 'white' : '#111827' }}>{stats.completed}</p>
        </button>
        
        {/* Pending Tile */}
        <button
          onClick={() => setActiveTab('pending')}
          style={{ 
            background: activeTab === 'pending' ? '#f59e0b' : 'white', 
            padding: '1.5rem', 
            borderRadius: '0.5rem', 
            border: activeTab === 'pending' ? '2px solid #f59e0b' : '1px solid #e5e7eb',
            cursor: 'pointer',
            transition: 'all 0.2s',
            textAlign: 'left'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <AlertCircle size={20} color={activeTab === 'pending' ? 'white' : '#f59e0b'} />
            <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: activeTab === 'pending' ? 'white' : '#6b7280' }}>Pending</h3>
          </div>
          <p style={{ fontSize: '2rem', fontWeight: '700', color: activeTab === 'pending' ? 'white' : '#111827' }}>{stats.pending}</p>
        </button>
      </div>

      {/* CSV Upload Section */}
      {uploadMode && (
        <div style={{ background: '#f0f9ff', border: '1px solid #0ea5e9', borderRadius: '0.5rem', padding: '1.5rem', marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#0c4a6e' }}>Upload Inventory CSV</h3>
            <button
              onClick={() => {
                setUploadMode(false);
                resetImportState();
              }}
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            >
              <X size={20} color="#6b7280" />
            </button>
          </div>
          
          {!previewData && (
          <form onSubmit={handleFileUpload}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Select CSV File
              </label>
            <div style={{ marginBottom: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                This will import from your clinical devices CSV with the following mapping:
                <br />
                <strong>number → Unique System Number</strong> (for duplicate checking)
                <br />
                <strong>name → Asset Tag</strong> (displayed in table)
                <br />
                <strong>asset_manufacturer → Manufacturer</strong>
                <br />
                <strong>serial_number → Serial #</strong>
                <br />
                <strong>model_name → Model</strong>
                <br />
                <strong>asset_type → Type</strong>
                <br />
                <strong>asset_location → Location</strong>
                <br />
                <strong>u_location_vicinity → Vicinity</strong>
                <br />
                <strong>u_next_scheduled_maint_date → PM Date</strong>
                <br />
                <br />
                <div style={{ 
                  background: '#f0f9ff', 
                  border: '1px solid #0ea5e9', 
                  borderRadius: '0.375rem', 
                  padding: '0.75rem',
                  marginTop: '0.5rem'
                }}>
                  <strong style={{ color: '#0c4a6e' }}>🚀 Auto-Creation Feature:</strong>
                  <br />
                  <span style={{ color: '#0c4a6e' }}>
                    New assets will automatically create corresponding Fixed Asset records, 
                    eliminating double data entry between Inventory and Fixed Assets systems!
                  </span>
                </div>
                <br />
                <em>Duplicate assets will be skipped automatically.</em>
              </div>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setUploadFile(e.target.files[0])}
                style={{ 
                  width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', 
                  borderRadius: '0.375rem', background: 'white' 
                }}
                required
              />
            </div>
            
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                type="submit"
                disabled={!uploadFile || uploadProgress?.status === 'uploading'}
                style={{
                  padding: '0.5rem 1rem', background: '#2563eb', color: 'white',
                  border: 'none', borderRadius: '0.375rem', cursor: 'pointer',
                  opacity: (!uploadFile || uploadProgress?.status === 'uploading') ? 0.5 : 1
                }}
              >
                {uploadProgress?.status === 'uploading' ? 'Analyzing...' : 'Preview Import'}
              </button>
            </div>
          </form>
          )}

          {previewData && (
            <div style={{ background: 'white', border: '1px solid #d1d5db', borderRadius: '0.375rem', padding: '1rem', marginBottom: '1rem' }}>
              <h4 style={{ margin: '0 0 0.75rem 0', color: '#111827' }}>Import Preview</h4>
              <p style={{ margin: '0 0 1rem 0', color: '#6b7280', fontSize: '0.9rem' }}>
                Review what will change before committing. Nothing has been written to the database yet.
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
                <div style={{ background: '#ecfdf5', border: '1px solid #10b981', borderRadius: '0.375rem', padding: '0.75rem' }}>
                  <div style={{ fontSize: '0.75rem', color: '#065f46', fontWeight: 600 }}>WILL ADD</div>
                  <div style={{ fontSize: '1.5rem', color: '#047857', fontWeight: 700 }}>{previewData.will_add}</div>
                </div>
                <div style={{ background: '#eff6ff', border: '1px solid #3b82f6', borderRadius: '0.375rem', padding: '0.75rem' }}>
                  <div style={{ fontSize: '0.75rem', color: '#1e3a8a', fontWeight: 600 }}>WILL UPDATE</div>
                  <div style={{ fontSize: '1.5rem', color: '#1d4ed8', fontWeight: 700 }}>{previewData.will_update}</div>
                </div>
                <div style={{ background: '#fef2f2', border: '1px solid #ef4444', borderRadius: '0.375rem', padding: '0.75rem' }}>
                  <div style={{ fontSize: '0.75rem', color: '#7f1d1d', fontWeight: 600 }}>WILL DELETE</div>
                  <div style={{ fontSize: '1.5rem', color: '#b91c1c', fontWeight: 700 }}>{previewData.will_delete}</div>
                  <div style={{ fontSize: '0.7rem', color: '#7f1d1d', marginTop: '0.15rem' }}>
                    {previewData.will_delete_pct_of_inventory}% of {previewData.total_inventory}
                  </div>
                </div>
                <div style={{ background: '#f3f4f6', border: '1px solid #9ca3af', borderRadius: '0.375rem', padding: '0.75rem' }}>
                  <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: 600 }}>UNCHANGED</div>
                  <div style={{ fontSize: '1.5rem', color: '#4b5563', fontWeight: 700 }}>{previewData.will_skip_no_changes}</div>
                </div>
              </div>

              {Array.isArray(previewData.warnings) && previewData.warnings.length > 0 && (
                <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '0.375rem', padding: '0.75rem', marginBottom: '1rem' }}>
                  {previewData.warnings.map((w, idx) => (
                    <div key={idx} style={{ color: '#92400e', fontSize: '0.9rem' }}>⚠️ {w}</div>
                  ))}
                </div>
              )}

              {Array.isArray(previewData.sample_deletes) && previewData.sample_deletes.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.85rem', color: '#374151', fontWeight: 600, marginBottom: '0.35rem' }}>
                    Sample of devices that will be deleted (first {previewData.sample_deletes.length}
                    {previewData.will_delete > previewData.sample_deletes.length ? ` of ${previewData.will_delete}` : ''}):
                  </div>
                  <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '0.375rem', padding: '0.5rem 0.75rem', maxHeight: '160px', overflowY: 'auto', fontFamily: 'monospace', fontSize: '0.8rem', color: '#374151' }}>
                    {previewData.sample_deletes.map((d, idx) => (
                      <div key={idx}>{d.asset}{d.name ? ` — ${d.name}` : ''}</div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => { setPreviewData(null); setPreviewToken(null); }}
                  disabled={committing}
                  style={{
                    padding: '0.5rem 1rem', background: 'white', color: '#374151',
                    border: '1px solid #d1d5db', borderRadius: '0.375rem', cursor: committing ? 'not-allowed' : 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCommitImport}
                  disabled={committing}
                  style={{
                    padding: '0.5rem 1rem', background: '#dc2626', color: 'white',
                    border: 'none', borderRadius: '0.375rem', cursor: committing ? 'not-allowed' : 'pointer',
                    opacity: committing ? 0.6 : 1
                  }}
                >
                  {committing ? 'Importing...' : 'Confirm Import'}
                </button>
              </div>
            </div>
          )}

          {uploadProgress && (
            <div style={{ 
              marginTop: '1rem', padding: '0.75rem', borderRadius: '0.375rem',
              background: uploadProgress.status === 'success' ? '#dcfce7' : uploadProgress.status === 'error' ? '#fef2f2' : '#dbeafe',
              color: uploadProgress.status === 'success' ? '#166534' : uploadProgress.status === 'error' ? '#991b1b' : '#1e40af'
            }}>
              {uploadProgress.message}
            </div>
          )}
        </div>
      )}

      {/* Advanced Filters */}
      {showFilters && (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1.5rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600' }}>Advanced Filters</h3>
            <button
              onClick={clearFilters}
              style={{ 
                padding: '0.25rem 0.5rem', background: '#6b7280', color: 'white', 
                border: 'none', borderRadius: '0.25rem', fontSize: '0.75rem', cursor: 'pointer' 
              }}
            >
              Clear All
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>Status</label>
              <select
                value={filters.complete}
                onChange={(e) => setFilters(prev => ({ ...prev, complete: e.target.value }))}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
              >
                <option value="">All</option>
                <option value="yes">Completed</option>
                <option value="no">Pending</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>Manufacturer</label>
              <input
                type="text"
                value={filters.manufacturer}
                onChange={(e) => setFilters(prev => ({ ...prev, manufacturer: e.target.value }))}
                placeholder="Filter by manufacturer..."
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>Location</label>
              <input
                type="text"
                value={filters.location}
                onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
                placeholder="Filter by location..."
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>Asset Type</label>
              <input
                type="text"
                value={filters.assetType}
                onChange={(e) => setFilters(prev => ({ ...prev, assetType: e.target.value }))}
                placeholder="Filter by asset type..."
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ position: 'relative', maxWidth: '500px' }}>
          <Search size={20} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            type="text"
            placeholder="Search inventory, part numbers, cables..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 0.75rem 0.75rem 2.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              fontSize: '0.875rem'
            }}
          />
        </div>
      </div>

      {/* Part Search Results Banner */}
      {partSearchResults && partSearchResults.length > 0 && (
        <div style={{ 
          marginBottom: '1.5rem', 
          padding: '1rem', 
          background: '#dbeafe', 
          border: '1px solid #3b82f6',
          borderRadius: '0.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <div style={{ fontWeight: '600', color: '#1e40af', marginBottom: '0.5rem' }}>
              🔌 Found {partSearchResults.length} part{partSearchResults.length > 1 ? 's' : ''} matching "{searchTerm}"
            </div>
            <div style={{ fontSize: '0.875rem', color: '#1e3a8a' }}>
              {partSearchResults.map((part, index) => (
                <span key={part.id}>
                  {part.part_number} ({part.part_description})
                  {index < partSearchResults.length - 1 ? ', ' : ''}
                </span>
              ))}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#1e40af', marginTop: '0.25rem' }}>
              Showing equipment that uses {partSearchResults.length === 1 ? 'this part' : 'these parts'}
            </div>
          </div>
          <button
            onClick={clearPartSearch}
            style={{ 
              padding: '0.5rem', 
              background: '#3b82f6', 
              color: 'white', 
              border: 'none', 
              borderRadius: '0.375rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <X size={16} />
            Clear
          </button>
        </div>
      )}

      {/* Results count */}
      <div style={{ marginBottom: '1rem', color: '#6b7280', fontSize: '0.875rem' }}>
        Showing {currentItems.length} of {filteredInventory.length} inventory items
      </div>

      {/* Table */}
      <div style={{ background: 'white', borderRadius: '0.5rem', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: '0.875rem' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>
                  <button
                    onClick={() => handleSort('complete')}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600' }}
                  >
                    Status
                  </button>
                </th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>
                  <button
                    onClick={() => handleSort('asset')}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600' }}
                  >
                    Asset Tag
                  </button>
                </th>
                <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600' }}>Parts</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Manufacturer & Model</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Serial & Type</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Location & Vicinity</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>PM Date</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Frequency</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Last Updated</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                    Loading inventory...
                  </td>
                </tr>
              ) : currentItems.length === 0 ? (
                <tr>
                  <td colSpan="10" style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                    No inventory items found matching your criteria
                  </td>
                </tr>
              ) : (
                currentItems.map(item => (
                  <tr 
                    key={item.id} 
                    style={{ 
                      borderTop: '1px solid #f3f4f6',
                      backgroundColor: item.complete === 'YES' ? '#fef3c7' : 'white'
                    }}
                  >
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {item.complete === 'YES' ? (
                          <CheckCircle size={16} color="#16a34a" />
                        ) : (
                          <AlertCircle size={16} color="#f59e0b" />
                        )}
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          background: item.complete === 'YES' ? '#dcfce7' : '#fef3c7',
                          color: item.complete === 'YES' ? '#166534' : '#92400e'
                        }}>
                          {item.complete === 'YES' ? 'Complete' : 'Pending'}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ fontWeight: '500', color: '#2563eb' }}>{item.name || item.number_field}</div>
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      <PartsBadge 
                        item={item} 
                        partCount={partsCounts[`${item.type}|${item['asset.asset_manufacturer']}|${item.model || ''}`]}
                        onClick={() => openPartsModal(item)} 
                      />
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ fontWeight: '500' }}>{item['asset.asset_manufacturer']}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{item.model}</div>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div>{item.serial}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{item.type}</div>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ fontWeight: '500' }}>{item.work_location}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{item.vicinity}</div>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <Calendar size={14} color="#6b7280" />
                        <span>{formatDate(item.u_next_scheduled_maint_date)}</span>
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {item.pm_frequency ? (
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          background: '#eff6ff',
                          color: '#1e40af'
                        }}>
                          {item.pm_frequency}
                        </span>
                      ) : (
                        <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>N/A</span>
                      )}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        {formatDate(item.inserttime)}
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {item.complete !== 'YES' && (
                        <button
                          onClick={() => markComplete(item.id)}
                          style={{ 
                            padding: '0.25rem 0.5rem', background: '#16a34a', color: 'white', 
                            border: 'none', borderRadius: '0.25rem', fontSize: '0.75rem', cursor: 'pointer' 
                          }}
                        >
                          Mark Complete
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ padding: '1rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              Page {currentPage} of {totalPages}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  background: 'white',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 1 ? 0.5 : 1
                }}
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  background: 'white',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  opacity: currentPage === totalPages ? 0.5 : 1
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Parts Modal */}
        {showPartsModal && selectedEquipment && (
          <div style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            background: 'rgba(0,0,0,0.5)', 
            zIndex: 1000, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            padding: '1rem'
          }}>
            <div style={{ 
              background: 'white', 
              borderRadius: '0.5rem', 
              padding: '2rem', 
              maxWidth: '900px', 
              width: '100%', 
              maxHeight: '90vh', 
              overflow: 'auto' 
            }}>
              {/* Modal Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
                <div>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                    🔌 Parts & Cables
                  </h3>
                  <p style={{ color: '#6b7280', marginBottom: '0.5rem' }}>
                    <strong>{selectedEquipment.type}</strong>
                    {' - '}
                    {selectedEquipment['asset.asset_manufacturer']}
                    {selectedEquipment.model && ` ${selectedEquipment.model}`}
                  </p>
                  <p style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                    This information applies to all equipment of this type/model
                  </p>
                </div>
                <button 
                  onClick={() => setShowPartsModal(false)} 
                  style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  <X size={24} />
                </button>
              </div>

              {/* Parts List */}
              {partsLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                  Loading parts...
                </div>
              ) : equipmentParts.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '3rem', 
                  background: '#f9fafb', 
                  borderRadius: '0.5rem',
                  marginBottom: '1rem'
                }}>
                  <Cable size={48} color="#9ca3af" style={{ margin: '0 auto 1rem' }} />
                  <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
                    No parts documented yet for this equipment type
                  </p>
                  <button
                    onClick={() => openPartForm()}
                    style={{ 
                      padding: '0.5rem 1rem', 
                      background: '#2563eb', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '0.375rem', 
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <Plus size={16} />
                    Add First Part
                  </button>
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: '1rem' }}>
                    <button
                      onClick={() => openPartForm()}
                      style={{ 
                        padding: '0.5rem 1rem', 
                        background: '#2563eb', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '0.375rem', 
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.875rem'
                      }}
                    >
                      <Plus size={16} />
                      Add Part
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {equipmentParts.map(part => (
                      <div
                        key={part.id}
                        style={{
                          padding: '1rem',
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.5rem',
                          background: '#fafafa',
                          display: 'grid',
                          gridTemplateColumns: '2fr 2fr 1fr auto',
                          gap: '1rem',
                          alignItems: 'center'
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                            {part.part_description}
                          </div>
                          {part.part_manufacturer && (
                            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                              {part.part_manufacturer}
                            </div>
                          )}
                        </div>
                        <div>
                          <div style={{ 
                            fontFamily: 'monospace', 
                            fontSize: '0.875rem',
                            background: '#e0e7ff',
                            color: '#3730a3',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '0.25rem',
                            display: 'inline-block',
                            fontWeight: '600'
                          }}>
                            {part.part_number}
                          </div>
                          {part.supplier && (
                            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                              Supplier: {part.supplier}
                            </div>
                          )}
                        </div>
                        <div style={{ fontSize: '0.875rem' }}>
                          <div>Qty: {part.quantity_per_unit}</div>
                          {part.unit_cost && (
                            <div style={{ fontWeight: '500', color: '#16a34a' }}>
                              ${parseFloat(part.unit_cost).toFixed(2)}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => openPartForm(part)}
                            style={{
                              padding: '0.5rem',
                              background: '#f59e0b',
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.375rem',
                              cursor: 'pointer'
                            }}
                            title="Edit"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            onClick={() => deletePart(part.id)}
                            style={{
                              padding: '0.5rem',
                              background: '#dc2626',
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.375rem',
                              cursor: 'pointer'
                            }}
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Part Form */}
              {showPartForm && (
                <div style={{ 
                  marginTop: '1.5rem', 
                  padding: '1.5rem', 
                  background: '#f0f9ff', 
                  border: '1px solid #0ea5e9',
                  borderRadius: '0.5rem' 
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h4 style={{ fontSize: '1.125rem', fontWeight: '600' }}>
                      {editingPart ? 'Edit Part' : 'Add Part'}
                    </h4>
                    <button onClick={closePartForm} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                      <X size={20} />
                    </button>
                  </div>

                  <form onSubmit={submitPartForm}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.875rem' }}>
                          Part/Cable Description *
                        </label>
                        <input
                          type="text"
                          name="part_description"
                          value={partFormData.part_description}
                          onChange={handlePartFormChange}
                          required
                          placeholder="e.g., Power Cable, USB Cable"
                          style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.875rem' }}>
                          Part Number *
                        </label>
                        <input
                          type="text"
                          name="part_number"
                          value={partFormData.part_number}
                          onChange={handlePartFormChange}
                          required
                          placeholder="e.g., PHI-PWR-HD15"
                          style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.875rem' }}>
                          Part Manufacturer
                        </label>
                        <input
                          type="text"
                          name="part_manufacturer"
                          value={partFormData.part_manufacturer}
                          onChange={handlePartFormChange}
                          placeholder="e.g., Philips"
                          style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.875rem' }}>
                          Quantity per Unit
                        </label>
                        <input
                          type="number"
                          name="quantity_per_unit"
                          value={partFormData.quantity_per_unit}
                          onChange={handlePartFormChange}
                          min="1"
                          style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.875rem' }}>
                          Unit Cost ($)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          name="unit_cost"
                          value={partFormData.unit_cost}
                          onChange={handlePartFormChange}
                          placeholder="0.00"
                          style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.875rem' }}>
                          Supplier
                        </label>
                        <input
                          type="text"
                          name="supplier"
                          value={partFormData.supplier}
                          onChange={handlePartFormChange}
                          placeholder="e.g., Amazon, Grainger"
                          style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                        />
                      </div>
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.875rem' }}>
                        Supplier Link (optional)
                      </label>
                      <input
                        type="url"
                        name="supplier_link"
                        value={partFormData.supplier_link}
                        onChange={handlePartFormChange}
                        placeholder="https://..."
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                      />
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.875rem' }}>
                        Notes
                      </label>
                      <textarea
                        name="notes"
                        value={partFormData.notes}
                        onChange={handlePartFormChange}
                        rows="3"
                        placeholder="Any additional information..."
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', resize: 'vertical' }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      <button
                        type="button"
                        onClick={closePartForm}
                        style={{ 
                          flex: 1,
                          padding: '0.75rem', 
                          border: '1px solid #d1d5db', 
                          borderRadius: '0.375rem', 
                          background: 'white', 
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        style={{ 
                          flex: 1,
                          padding: '0.75rem', 
                          background: '#2563eb', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: '0.375rem', 
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                      >
                        {editingPart ? 'Update Part' : 'Add Part'}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NRMCInventory;